import {useEffect, useState} from 'react'
import {Button, Col, Form, Row} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import Select from 'react-select'
import {TFormInfoContact, TFormMemberPolicy} from '../policiesTypes'
import {useLazyQuery} from '@apollo/client'
import {GET_ALL_CONTACTS_POLICY_HOLDER_SEARCH} from 'app/tfi/gql/queries/contactsApiQueries'
import {toast} from 'react-toastify'
import {useDebounce} from '../../../../../_metronic/helpers'
import {calculateAge} from '../../../helpers'
import {FormikErrors, FormikTouched} from 'formik'
import { GENDER_OPTIONS } from '../../../helpers/consts'
import { DateInput } from 'app/tfi/components/DateInput'
import { PhoneInput } from 'app/tfi/components/PhoneInput'
import {IResContactsAPI, NodeContact} from 'app/tfi/types/contactsApi'

type ContactOption = {
  label: string
  value: string
  contact: NodeContact
}

type PolicyOwnershipValues = {
  ownsCar?: boolean | null
  ownsHome?: boolean | null
}

export const PolicyHolderTabModal: React.FC<{
  onPrevStep?: () => void
  onNextStep: () => void
  handleChange: (field: string, value: any) => void
  handleBlur: (field: string) => void
  values: TFormMemberPolicy | TFormInfoContact
  errors: FormikErrors<TFormMemberPolicy | TFormInfoContact>
  touched: FormikTouched<TFormMemberPolicy | TFormInfoContact>
  policyValues?: PolicyOwnershipValues
  policyErrors?: FormikErrors<any>
  policyTouched?: FormikTouched<any>
  onPolicyChange?: (field: keyof PolicyOwnershipValues, value: boolean | null) => void
  onPolicyBlur?: (field: keyof PolicyOwnershipValues) => void
  submitCount?: number
  isContactModule?: boolean
  showSearchContact?: boolean
}> = ({
  onNextStep,
  onPrevStep,
  handleBlur,
  handleChange,
  values,
  errors,
  touched,
  policyValues,
  policyErrors,
  policyTouched,
  onPolicyChange,
  onPolicyBlur,
  submitCount,
  isContactModule,
  showSearchContact,
}) => {
  const intl = useIntl()
  const [gqlSearchContacts, {data: dataContacts, loading, error}] =
    useLazyQuery<IResContactsAPI>(GET_ALL_CONTACTS_POLICY_HOLDER_SEARCH, {
      fetchPolicy: 'no-cache',
    })
  const [inputSelect, setInputSelect] = useState('')
  const valueDeb = useDebounce(inputSelect, 500)
  const [options, setOptions] = useState<ContactOption[]>([])
  const [selectedContact, setSelectedContact] = useState<ContactOption | null>(null)
  const [tab, setTab] = useState<'new' | 'existing'>('new')
  const [holderApplicants, setHolderApplicants] = useState<string>('yes')

  useEffect(() => {
    if (tab === 'new') {
      setSelectedContact(null)
      setInputSelect('')
    }
  }, [tab])

  useEffect(() => {
    if (error)
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: error.message}
        )
      )
  }, [error, intl])

  useEffect(() => {
    if (!dataContacts?.allContacts?.edges) return
    const mapped = dataContacts.allContacts.edges.map(({node}) => {
      const name =
        node.fullName ||
        `${node.firstName || ''} ${node.lastName || ''}`.replace(/\s+/g, ' ').trim()
      const phone = node.phone ? ` - ${node.phone}` : ''
      return {
        label: `${name}${phone}`,
        value: node.id,
        contact: node,
      }
    })
    setOptions(mapped)
  }, [dataContacts])

  useEffect(() => {
    if (tab !== 'existing') return
    if (!valueDeb || valueDeb.trim().length < 4) return
    gqlSearchContacts({
      variables: {
        name: valueDeb.trim(),
        first: 20,
      },
    })
  }, [gqlSearchContacts, tab, valueDeb])

  const applyContactToForm = (contact: NodeContact) => {
    const fallback = (val?: string | null) => val ?? ''

    if ('id' in values) handleChange('id', fallback(contact.id))
    if ('firstName' in values) handleChange('firstName', fallback(contact.firstName))
    if ('middleName' in values) handleChange('middleName', fallback(contact.middleName))
    if ('lastName' in values) handleChange('lastName', fallback(contact.lastName))
    if ('secondLastName' in values) handleChange('secondLastName', fallback(contact.secondLastName))
    if ('preferredLenguage' in values) handleChange('preferredLenguage', fallback(contact.preferredLenguage))

    if ('dateBirth' in values) handleChange('dateBirth', fallback(contact.dateBirth || ''))
    if ('gender' in values) handleChange('gender', fallback(contact.gender))

    if ('phone' in values) handleChange('phone', fallback(contact.phone))

    if (isContactModule) {
      if ('email' in values) handleChange('email', fallback(contact.email))
      if ('fullName' in values) handleChange('fullName', fallback(contact.fullName))
      if ('stateEUId' in values) handleChange('stateEUId', fallback(contact.stateEU?.id))
      if ('zipCode' in values) handleChange('zipCode', fallback(contact.zipCode))
      if ('city' in values) handleChange('city', fallback(contact.city))
      if ('address1' in values) handleChange('address1', fallback(contact.address1))
      if ('address2' in values) handleChange('address2', fallback(contact.address2))
      if ('immigrationStatus' in values) handleChange('immigrationStatus', fallback(contact.immigrationStatus))
    } else {
      if ('emailAddress' in values) handleChange('emailAddress', fallback(contact.email))
    }
  }

  const handleSelectContact = (option: ContactOption | null) => {
    setSelectedContact(option)
    if (!option) return
    applyContactToForm(option.contact)
  }

  const showError = (field: string) => (touched[field] || (submitCount ?? 0) > 0) && errors[field]

  const showPolicyError = (field: keyof PolicyOwnershipValues) =>
    (((policyTouched as any)?.[field] || (submitCount ?? 0) > 0) &&
      (policyErrors as any)?.[field]) ||
    null

  const toSelectValue = (value?: boolean | null) => {
    if (value === true) return 'true'
    if (value === false) return 'false'
    return ''
  }

  const handlePolicySelect = (field: keyof PolicyOwnershipValues, value: string) => {
    if (!onPolicyChange) return
    const parsed = value === '' ? null : value === 'true'
    onPolicyChange(field, parsed)
  }

  const selectPlaceholder = intl.formatMessage({
    id: 'common.placeholder.selectOption',
    defaultMessage: 'Select an option',
  })
  const requiredHint = intl.formatMessage({id: 'common.hint.required', defaultMessage: '(required)'})

  // console.log('showSearchContact:' + showSearchContact)

  return (
    <div className='contact-information'>
      {showSearchContact && (
        <div className='search-contact-policy-form'>
          <Button onClick={() => setTab(tab === 'new' ? 'existing' : 'new')} variant='primary'>
            {tab === 'new' ? (
              <i className='bi bi-person-check' />
            ) : (
              <i className='bi bi-person-plus' />
            )}
            {tab === 'new' ? (
              <FormattedMessage
                id='policies.list.wizard.holder.existingContact'
                defaultMessage='SELECT EXISTING CONTACT'
              />
            ) : (
              <FormattedMessage
                id='policies.list.wizard.holder.newContact'
                defaultMessage='NEW CONTACT'
              />
            )}
          </Button>

          {tab === 'existing' && (
            <div className='existing-contact'>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.findContact'
                    defaultMessage='Find and select a contact'
                  />
                </Form.Label>
                <Select
                  isLoading={loading}
                  isClearable
                  loadingMessage={() =>
                    intl.formatMessage({
                      id: 'policies.list.wizard.holder.loadingContacts',
                      defaultMessage: 'Loading contacts...',
                    })
                  }
                  noOptionsMessage={() =>
                    inputSelect.trim().length < 4
                      ? intl.formatMessage({
                          id: 'policies.list.wizard.holder.typeToSearch',
                          defaultMessage: 'Type at least 4 characters to search',
                        })
                      : intl.formatMessage({
                          id: 'policies.list.wizard.holder.noContacts',
                          defaultMessage: 'No contacts found',
                        })
                  }
                  placeholder={intl.formatMessage({
                    id: 'policies.list.wizard.holder.searchContactPlaceholder',
                    defaultMessage: 'Search and select a contact...',
                  })}
                  filterOption={() => true}
                  options={options}
                  value={selectedContact}
                  inputValue={inputSelect}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      const query = inputSelect.trim()
                      if (query.length >= 4) {
                        gqlSearchContacts({
                          variables: {
                            name: query,
                            first: 20,
                          },
                        })
                      }
                    }
                  }}
                  onInputChange={(value, meta) => {
                    if (meta.action === 'input-change') {
                      setInputSelect(value)
                    } else if (meta.action === 'set-value') {
                      setInputSelect('')
                    }
                  }}
                  onChange={(option) => handleSelectContact(option as ContactOption | null)}
                />
              </Form.Group>
              <Form.Text style={{fontSize: '12px'}}>
                <FormattedMessage
                  id='policies.list.wizard.holder.searchHelp'
                  defaultMessage='Search by name, email address, phone number or last 4 digits of SSN'
                />
              </Form.Text>
            </div>
          )}
        </div>
      )}

      <div className='title-section-policy-form'>
        {isContactModule ? (
          <h6>
            <FormattedMessage
              id='policies.list.wizard.holder.contactInfoTitle'
              defaultMessage='Contact Information'
            /> </h6>
        ) : (
          <h6>
            <FormattedMessage
              id='policies.list.wizard.holder.policyholderInfoTitle'
              defaultMessage='Policyholder Information'
            /> </h6>
        )}
      </div>
      <div>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id='common.field.firstName' defaultMessage='First Name' /> <small className='text-danger'>
                  {'firstName' in values &&
                    showError('firstName') &&
                    typeof errors.firstName === 'string' && <>{errors.firstName}</>}
                </small>
              </Form.Label>
              <Form.Control
                placeholder={intl.formatMessage({
                  id: 'common.placeholder.enterFirstName',
                  defaultMessage: 'Enter first name',
                })}
                onChange={(e) => handleChange('firstName', e.target.value)}
                value={values.firstName ?? ''}
                onBlur={() => handleBlur('firstName')}
                className={touched.firstName && errors.firstName ? 'input-error' : ''}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id='common.field.middleName' defaultMessage='Middle Name' />
              </Form.Label>
              <Form.Control
                placeholder={intl.formatMessage({
                  id: 'common.placeholder.enterMiddleName',
                  defaultMessage: 'Enter middle name',
                })}
                onChange={(e) => handleChange('middleName', e.target.value)}
                onBlur={() => handleBlur('middleName')}
                value={values.middleName ?? ''}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id='common.field.lastName' defaultMessage='Last Name' /> <small className='text-danger'>
                  {'lastName' in values &&
                    touched.lastName &&
                    typeof errors.lastName === 'string' && <>{errors.lastName}</>}
                </small>
              </Form.Label>
              <Form.Control
                placeholder={intl.formatMessage({
                  id: 'common.placeholder.enterLastName',
                  defaultMessage: 'Enter last name',
                })}
                onChange={(e) => handleChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
                value={values.lastName ?? ''}
                className={touched.lastName && errors.lastName ? 'input-error' : ''}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='common.field.secondLastName'
                  defaultMessage='Second Last Name'
                />
              </Form.Label>
              <Form.Control
                placeholder={intl.formatMessage({
                  id: 'common.placeholder.enterSecondLastName',
                  defaultMessage: 'Enter second last name',
                })}
                onChange={(e) => handleChange('secondLastName', e.target.value)}
                value={values.secondLastName ?? ''}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <DateInput
                id="dateBirth"
                name="dateBirth"
                label={intl.formatMessage({
                  id: 'common.field.dateOfBirth',
                  defaultMessage: 'Date of Birth',
                })}
                value={values.dateBirth}
                error={errors.dateBirth as string}
                touched={touched.dateBirth as boolean}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {values.dateBirth && (
                <div className="label-dob">
                  <FormattedMessage
                    id='policies.list.wizard.holder.ageYears'
                    defaultMessage='Age: {age} years old'
                    values={{age: calculateAge(values.dateBirth)}}
                  />
                </div>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id='common.field.gender' defaultMessage='Gender' /> <small className='text-danger'>
                  {'gender' in values && touched.gender && typeof errors.gender === 'string' && (
                    <>{errors.gender}</>
                  )}
                </small>
              </Form.Label>
              <Form.Select
                onChange={(e) => handleChange('gender', e.target.value)}
                onBlur={() => handleBlur('gender')}
                value={values.gender ?? ''}
                className={touched.gender && errors.gender ? 'input-error' : ''}
                required
              >
                <option value=''>{selectPlaceholder}</option>
                {GENDER_OPTIONS.map(({value, label}) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <PhoneInput
              id="phone"
              name="phone"
              label={intl.formatMessage({id: 'common.field.phone', defaultMessage: 'Phone'})}
              value={values.phone}
              error={errors.phone as string}
              touched={touched.phone as boolean}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id='common.field.email' defaultMessage='Email' /> {isContactModule ? (
                  <small className='text-danger'>
                    {'email' in values && touched.email && typeof errors.email === 'string' && (
                      <>{errors.email}</>
                    )}
                  </small>
                ) : (
                  <small className='text-danger'>
                    {'emailAddress' in values &&
                      touched.emailAddress &&
                      typeof errors.emailAddress === 'string' && <>{errors.emailAddress}</>}
                  </small>
                )}
              </Form.Label>
              {isContactModule ? (
                <Form.Control
                  type='email'
                  placeholder={intl.formatMessage({
                    id: 'common.placeholder.enterEmail',
                    defaultMessage: 'Enter email',
                  })}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  value={values.email ?? ''}
                  className={touched.email && errors.email ? 'input-error' : ''}
                  required
                />
              ) : (
                <Form.Control
                  type='email'
                  placeholder={intl.formatMessage({
                    id: 'common.placeholder.enterEmail',
                    defaultMessage: 'Enter email',
                  })}
                  onChange={(e) => handleChange('emailAddress', e.target.value)}
                  onBlur={() => handleBlur('emailAddress')}
                  value={values.emailAddress ?? ''}
                  className={touched.emailAddress && errors.emailAddress ? 'input-error' : ''}
                  required
                />
              )}
            </Form.Group>
          </Col>
        </Row>
        {isContactModule && (
          <Row className='mt-2'>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.preferredLanguage'
                    defaultMessage='Preferred Language'
                  />
                </Form.Label>
                <Form.Select
                  onChange={(e) => handleChange('preferredLenguage', e.target.value)}
                  onBlur={() => handleBlur('preferredLenguage')}
                  value={'preferredLenguage' in values ? (values.preferredLenguage ?? '') : ''}
                >
                  <option value=''>{selectPlaceholder}</option>
                  <option value='Spanish'>
                    {intl.formatMessage({id: 'common.language.spanish', defaultMessage: 'Spanish'})}
                  </option>
                  <option value='English'>
                    {intl.formatMessage({id: 'common.language.english', defaultMessage: 'English'})}
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.immigrationStatus'
                    defaultMessage='Immigration Status'
                  />
                </Form.Label>
                <Form.Select
                  onChange={(e) => handleChange('immigrationStatus', e.target.value)}
                  onBlur={() => handleBlur('immigrationStatus')}
                  value={'immigrationStatus' in values ? (values.immigrationStatus ?? '') : ''}
                >
                  <option value=''>{selectPlaceholder}</option>
                  <option value='CITIZEN'>
                    {intl.formatMessage({
                      id: 'policies.list.wizard.holder.immigration.citizen',
                      defaultMessage: 'Citizen',
                    })}
                  </option>
                  <option value='RESIDENT'>
                    {intl.formatMessage({
                      id: 'policies.list.wizard.holder.immigration.resident',
                      defaultMessage: 'Resident',
                    })}
                  </option>
                  <option value='ASYLUM'>
                    {intl.formatMessage({
                      id: 'policies.list.wizard.holder.immigration.asylum',
                      defaultMessage: 'Asylum Seeker',
                    })}
                  </option>
                  <option value='EAD'>
                    {intl.formatMessage({
                      id: 'policies.list.wizard.holder.immigration.ead',
                      defaultMessage: 'Employment Authorization Document (EAD)',
                    })}
                  </option>
                  <option value='TPS'>
                    {intl.formatMessage({
                      id: 'policies.list.wizard.holder.immigration.tps',
                      defaultMessage: 'Temporary Protected Status (TPS)',
                    })}
                  </option>
                  <option value='HUMANITARIAN_PAROLE'>
                    {intl.formatMessage({
                      id: 'policies.list.wizard.holder.immigration.humanitarianParole',
                      defaultMessage: 'Humanitarian Parole',
                    })}
                  </option>
                  <option value='UNDOCUMENTED'>
                    {intl.formatMessage({
                      id: 'policies.list.wizard.holder.immigration.undocumented',
                      defaultMessage: 'Undocumented',
                    })}
                  </option>
                  <option value='OTHER'>
                    {intl.formatMessage({id: 'common.option.other', defaultMessage: 'Other'})}
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.annualIncome1'
                    defaultMessage='Annual Income 1'
                  />
                </Form.Label>
                <Form.Control
                  type='number'
                  min={0}
                  placeholder='0.00'
                  onChange={(e) => handleChange('annualIncome1', e.target.value)}
                  onBlur={() => handleBlur('annualIncome1')}
                  value={'annualIncome1' in values ? (values.annualIncome1 ?? 0) : 0}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.annualIncome2'
                    defaultMessage='Annual Income 2'
                  />
                </Form.Label>
                <Form.Control
                  type='number'
                  min={0}
                  placeholder='0.00'
                  onChange={(e) => handleChange('annualIncome2', e.target.value)}
                  onBlur={() => handleBlur('annualIncome2')}
                  value={'annualIncome2' in values ? (values.annualIncome2 ?? 0) : 0}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.annualIncome3'
                    defaultMessage='Annual Income 3'
                  />
                </Form.Label>
                <Form.Control
                  type='number'
                  min={0}
                  placeholder='0.00'
                  onChange={(e) => handleChange('annualIncome3', e.target.value)}
                  onBlur={() => handleBlur('annualIncome3')}
                  value={'annualIncome3' in values ? (values.annualIncome3 ?? 0) : 0}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Check
                type='switch'
                id='isImportant'
                label={intl.formatMessage({
                  id: 'policies.list.wizard.holder.markImportant',
                  defaultMessage: 'Mark as Important',
                })}
                checked={'isImportant' in values ? (values.isImportant ?? false) : false}
                onChange={(e) => handleChange('isImportant', e.target.checked)}
              />
            </Col>
          </Row>
        )}
        {!isContactModule && (
          <Row className='mt-2'>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.ownsCar'
                    defaultMessage='Does the member own a car?'
                  /> <small className='text-danger'>{showPolicyError('ownsCar') || requiredHint}</small>
                </Form.Label>
                <Form.Select
                  value={toSelectValue(policyValues?.ownsCar)}
                  onChange={(e) => handlePolicySelect('ownsCar', e.target.value)}
                  onBlur={() => onPolicyBlur?.('ownsCar')}
                  required
                >
                  <option value=''>{selectPlaceholder}</option>
                  <option value='true'>
                    {intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})}
                  </option>
                  <option value='false'>
                    {intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})}
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.ownsHome'
                    defaultMessage='Does the member own a home?'
                  /> <small className='text-danger'>{showPolicyError('ownsHome') || requiredHint}</small>
                </Form.Label>
                <Form.Select
                  value={toSelectValue(policyValues?.ownsHome)}
                  onChange={(e) => handlePolicySelect('ownsHome', e.target.value)}
                  onBlur={() => onPolicyBlur?.('ownsHome')}
                  required
                >
                  <option value=''>{selectPlaceholder}</option>
                  <option value='true'>
                    {intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})}
                  </option>
                  <option value='false'>
                    {intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})}
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        )}
        {!isContactModule && (
          <Row className='mt-2'>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.holder.isApplicant'
                    defaultMessage='Is this member an applicant?'
                  />
                </Form.Label>
                <Form.Select
                  value={holderApplicants}
                  onChange={(e) => setHolderApplicants(e.target.value)}
                >
                  <option value='yes'>
                    {intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})}
                  </option>
                  <option value='no'>
                    {intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})}
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        )}
      </div>
      <div className='btn-prev-next'>
        <div></div>
        <Button size='sm' onClick={onNextStep}>
          <FormattedMessage id='policies.list.wizard.nextStep' defaultMessage='NEXT STEP' />
        </Button>
      </div>
    </div>
  )
}
